import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { Spinner } from "../ui/spinner";

// We'll handle PDF rendering with an iframe since it's simpler and doesn't require additional libraries
// This approach avoids issues with react-pdf dependencies

function PdfViewer({ fileUrl }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Reset loading state when a new PDF is loaded
    if (fileUrl) {
      setLoading(true);
      // We'll use setTimeout to simulate a loading delay
      const timer = setTimeout(() => {
        setLoading(false);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [fileUrl]);

  // Since we're using an iframe, we don't need page navigation anymore
  // But we'll keep the zoom controls for the container

  function zoomIn() {
    setScale((prevScale) => Math.min(prevScale + 0.1, 2.0));
  }

  function zoomOut() {
    setScale((prevScale) => Math.max(prevScale - 0.1, 0.5));
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex justify-end items-center mb-2">
        <div className="flex space-x-2">
          <Button variant="outline" size="sm" onClick={zoomOut}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          <span className="font-medium text-sm py-1 px-3 bg-gray-100 rounded">
            {Math.round(scale * 100)}%
          </span>
          <Button variant="outline" size="sm" onClick={zoomIn}>
            <ZoomIn className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div
        className="flex-1 overflow-auto bg-gray-200 flex items-center justify-center"
        style={{ transform: `scale(${scale})`, transformOrigin: "center" }}
      >
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-80 z-10">
            <Spinner size="lg" />
          </div>
        )}

        {fileUrl ? (
          <iframe
            src={fileUrl}
            className="w-full h-full border-0"
            title="PDF Viewer"
            onLoad={() => setLoading(false)}
          />
        ) : (
          <div className="text-center text-gray-500">No PDF selected</div>
        )}
      </div>
    </div>
  );
}

export default PdfViewer;
